import type { ThemeDefinition } from "../types"
import { pair, sharedTokens } from "./_helpers"

/**
 * Clear Verdict - Light default theme
 * Personality: calm, editorial, trustworthy, easy on the eyes
 * Use: default tenant theme, long-form reading, onboarding, general legal work
 */
const theme: ThemeDefinition = {
  id: "clear-verdict",
  label: "Clear Verdict",
  brand: {
    appName: "ECA Triage Accelerator",
    shortName: "ECA",
  },
  tokens: {
    shared: {
      ...sharedTokens,
      "--theme-radius": "6px",
      "--theme-border-width": "1px",
      "--theme-shadow-opacity": "0.05",
      "--theme-font-sans": "'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif",
      "--theme-font-mono": "'Geist Mono', 'IBM Plex Mono', ui-monospace, monospace",
      "--theme-font-heading": "'Inter', ui-sans-serif, system-ui, sans-serif",
      "--theme-accent-ring": "hsl(var(--primary) / 0.18)",
      "--theme-app-title-color": "hsl(var(--foreground))",
    },
    light: {
      "--background": "210 20% 96%",
      "--foreground": "220 14% 10%",
      "--card": "0 0% 100%",
      "--card-foreground": "220 14% 10%",
      "--popover": "0 0% 100%",
      "--popover-foreground": "220 14% 10%",
      "--primary": "215 76% 45%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "214 20% 92%",
      "--secondary-foreground": "220 14% 10%",
      "--muted": "216 18% 91%",
      "--muted-foreground": "220 12% 36%",
      "--accent": "215 76% 45% / 0.06",
      "--accent-foreground": "215 76% 45%",
      "--destructive": "0 72% 51%",
      "--destructive-foreground": "0 0% 100%",
      "--success": "152 60% 30%",
      "--success-foreground": "0 0% 100%",
      "--warning": "38 92% 36%",
      "--warning-foreground": "0 0% 100%",
      "--info": "199 89% 48%",
      "--info-foreground": "0 0% 100%",
      "--border": "220 16% 82%",
      "--input": "220 16% 82%",
      "--ring": "215 76% 45%",
      "--sidebar-background": "213 20% 94%",
      "--sidebar-foreground": "220 14% 10%",
      "--sidebar-primary": "215 76% 45%",
      "--sidebar-primary-foreground": "0 0% 100%",
      "--sidebar-accent": "214 20% 92%",
      "--sidebar-accent-foreground": "220 14% 10%",
      "--sidebar-border": "220 13% 88%",
      "--sidebar-ring": "215 76% 45%",
      "--theme-shell-accent": "hsl(var(--primary))",
      "--theme-shell-accent-soft": "hsl(var(--primary) / 0.08)",
      "--theme-shell-border": "hsl(var(--border) / 0.9)",
      "--theme-shell-muted": "hsl(var(--muted) / 0.5)",
      "--theme-surface-elevated": "hsl(var(--card))",
      "--theme-surface-subtle": "hsl(var(--muted) / 0.5)",
      // Semantic surface tokens
      "--bg-canvas": "210 20% 96%",
      "--bg-surface": "0 0% 100%",
      "--bg-surface-alt": "214 20% 95%",
      "--bg-raised": "0 0% 100%",
      "--bg-inset": "216 18% 93%",
      "--bg-overlay": "0 0% 100%",
      "--text-primary": "220 14% 10%",
      "--text-secondary": "220 12% 36%",
      "--text-tertiary": "220 10% 54%",
      "--text-inverse": "0 0% 100%",
      "--border-ghost": "220 16% 88%",
      "--selection-bg": "215 76% 45% / 0.15",
      "--selection-text": "220 14% 10%",
      "--scrim": "rgba(0, 0, 0, 0.4)",
    },
    dark: {
      "--background": "220 14% 10%",
      "--foreground": "210 20% 95%",
      "--card": "220 14% 13%",
      "--card-foreground": "210 20% 95%",
      "--popover": "220 14% 13%",
      "--popover-foreground": "210 20% 95%",
      "--primary": "215 76% 56%",
      "--primary-foreground": "0 0% 100%",
      "--secondary": "220 14% 17%",
      "--secondary-foreground": "210 20% 95%",
      "--muted": "220 14% 16%",
      "--muted-foreground": "218 11% 60%",
      "--accent": "215 76% 56% / 0.14",
      "--accent-foreground": "215 76% 56%",
      "--destructive": "0 63% 40%",
      "--destructive-foreground": "0 0% 100%",
      "--success": "152 60% 40%",
      "--success-foreground": "0 0% 100%",
      "--warning": "38 92% 50%",
      "--warning-foreground": "0 0% 100%",
      "--info": "199 89% 56%",
      "--info-foreground": "0 0% 100%",
      "--border": "220 14% 20%",
      "--input": "220 14% 20%",
      "--ring": "215 76% 56%",
      "--sidebar-background": "220 14% 9%",
      "--sidebar-foreground": "210 20% 95%",
      "--sidebar-primary": "215 76% 56%",
      "--sidebar-primary-foreground": "0 0% 100%",
      "--sidebar-accent": "220 14% 16%",
      "--sidebar-accent-foreground": "210 20% 95%",
      "--sidebar-border": "220 14% 20%",
      "--sidebar-ring": "215 76% 56%",
      "--theme-shell-accent": "hsl(var(--primary))",
      "--theme-shell-accent-soft": "hsl(var(--primary) / 0.1)",
      "--theme-shell-border": "hsl(var(--border) / 0.8)",
      "--theme-shell-muted": "hsl(var(--muted) / 0.6)",
      "--theme-surface-elevated": "hsl(var(--card) / 0.98)",
      "--theme-surface-subtle": "hsl(var(--muted) / 0.4)",
      // Semantic surface tokens
      "--bg-canvas": "220 14% 10%",
      "--bg-surface": "220 14% 13%",
      "--bg-surface-alt": "220 14% 15%",
      "--bg-raised": "220 14% 16%",
      "--bg-inset": "220 14% 8%",
      "--bg-overlay": "220 14% 13%",
      "--text-primary": "210 20% 95%",
      "--text-secondary": "218 11% 60%",
      "--text-tertiary": "218 10% 45%",
      "--text-inverse": "220 14% 10%",
      "--border-ghost": "220 14% 17%",
      "--selection-bg": "215 76% 56% / 0.2",
      "--selection-text": "210 20% 95%",
      "--scrim": "rgba(0, 0, 0, 0.6)",
    },
  },
  palette: {
    clusters: ["#3b82f6", "#8b5cf6", "#22c55e", "#f59e0b", "#ec4899", "#06b6d4", "#f97316", "#a855f7", "#84cc16", "#14b8a6", "#ef4444", "#10b981", "#eab308", "#6366f1", "#fb923c", "#d946ef", "#4ade80", "#c084fc", "#65a30d", "#0ea5e9"],
    charts: ["hsl(var(--primary))", "#3b82f6", "#22c55e", "#f59e0b", "#8b5cf6", "#06b6d4", "#ec4899"],
    highlights: ["#EF4444", "#F97316", "#EAB308", "#22C55E", "#06B6D4", "#3B82F6", "#8B5CF6", "#EC4899"],
    annotations: ["#3B82F6", "#EF4444", "#F97316", "#EAB308", "#22C55E", "#06B6D4", "#8B5CF6", "#EC4899"],
    bookmarks: ["#3B82F6", "#EF4444", "#22C55E", "#EAB308", "#8B5CF6", "#06B6D4"],
    speakers: ["#3B82F6", "#EF4444", "#22C55E", "#EAB308", "#8B5CF6", "#06B6D4", "#F97316", "#EC4899"],
    chronology: ["#3b82f6", "#8b5cf6", "#ef4444", "#f59e0b", "#6b7280"],
    venn: [pair("rgba(59,130,246,0.22)", "#3B82F6"), pair("rgba(249,115,22,0.22)", "#F97316"), pair("rgba(34,197,94,0.22)", "#22C55E"), pair("rgba(139,92,246,0.22)", "#8B5CF6"), pair("rgba(236,72,153,0.22)", "#EC4899")],
    redaction: { pending: pair("rgba(234,179,8,0.3)", "rgb(234,179,8)"), approved: pair("rgba(34,197,94,0.3)", "rgb(34,197,94)"), rejected: pair("rgba(239,68,68,0.3)", "rgb(239,68,68)"), selection: pair("rgba(59,130,246,0.2)", "rgb(59,130,246)") },
    waveform: { primary: "#3B82F6", secondary: "#93C5FD", glow: "rgba(59,130,246,0.25)", success: "#22C55E", error: "#EF4444" },
    graphEntities: { person: pair("rgba(59,130,246,0.14)", "#3b82f6"), organization: pair("rgba(139,92,246,0.14)", "#8b5cf6"), email: pair("rgba(6,182,212,0.14)", "#06b6d4"), domain: pair("rgba(249,115,22,0.14)", "#f97316"), location: pair("rgba(34,197,94,0.14)", "#22c55e") },
    graphDocument: pair("rgba(59,130,246,0.08)", "#3b82f6"),
    graphDefaultEntity: pair("rgba(107,114,128,0.14)", "#6b7280"),
    lasso: { stroke: "#3b82f6", fill: "rgba(59,130,246,0.08)" },
    emphasis: "#3b82f6",
  },
}

export default theme
